import type { WorkspaceMembersResponseT } from '@mentimeter/http-clients';
import { core } from '@mentimeter/http-clients';
import { useCallback, useMemo, type ReactNode } from 'react';
import useSWRInfinite from 'swr/infinite';
import { InvalidationContextProvider } from './useInvalidateWorkspaceMembersPaginated';

const PAGE_SIZE = 50;

export const useWorkspaceMembersPaginated = (
  queryString: string | undefined,
) => {
  const getKey = (pageIndex: number) => {
    return `/workspaces/members${queryString}&offset=${encodeURIComponent(
      pageIndex * PAGE_SIZE,
    )}&limit=${encodeURIComponent(PAGE_SIZE)}`;
  };

  const {
    data,
    error,
    size: page,
    setSize: setPage,
    isValidating,
    mutate,
  } = useSWRInfinite<WorkspaceMembersResponseT>(
    getKey,
    async (url) => {
      const response = await core().get<WorkspaceMembersResponseT>(url);
      return response.data;
    },
    {
      shouldRetryOnError: false,
      revalidateFirstPage: false,
    },
  );

  const members = useMemo(
    () => data?.flatMap(({ data }) => data) || [],
    [data],
  );

  const isLoadingInitialData = !data && !error;
  const isLoadingMoreMembers =
    isLoadingInitialData ||
    (page > 0 && data && typeof data[page - 1] === 'undefined');
  const isRefreshing = isValidating && data?.length === page;

  const hasReachedLastPage = useMemo(() => {
    if (!data) return false;
    if (data.length === 0) return true;
    const lastPage = data.at(-1);
    if (!lastPage) return true;
    return lastPage.data.length < PAGE_SIZE;
  }, [data]);

  const loadMoreMembers = useCallback(() => {
    if (isLoadingMoreMembers || hasReachedLastPage) return;
    setPage(page + 1);
  }, [hasReachedLastPage, isLoadingMoreMembers, setPage, page]);

  const InvalidationProvider = useCallback(
    ({ children }: { children: ReactNode }) => (
      <InvalidationContextProvider invalidate={mutate}>
        {children}
      </InvalidationContextProvider>
    ),
    [mutate],
  );

  return {
    members,
    page,
    setPage,
    isValidating,
    isLoading: isLoadingInitialData || isLoadingMoreMembers || isRefreshing,
    loadMoreMembers,
    isLoadingInitialMembersData: isLoadingInitialData,
    error,
    mutate,
    InvalidationProvider,
  };
};
